import styled, { css } from 'styled-components';

import Icon from '../../../_shared/components/Icon';
import Label from '../../../_shared/components/Label';
import Detail from '../../../_shared/components/Detail';
import applyResponsiveVdpStyles from '../../../_shared/helpers/applyResponsiveVdpStyles';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

/**
 * Variant Definitions
 */
const StyledTransportSectionVariants = {
  mini: {
    main: css`
      margin: 10px 18px 15px;
      padding: 14px 12px;
    `,
    promo: css`
      margin-top: 15px;
      padding: 18px !important;
    `
  },
  expanded: {
    main: css`
      margin: 0 36px 0;
      padding: 18px;
    `,
    promo: css`
      padding: 18px;
    `
  },
  standard: css`
    background-color: ${({ theme }) => theme.COLORS.NEUTRAL};
  `,
  promo: css`
    border: 2px solid ${({ theme }) => theme.COLORS.BRAND};
    border-radius: 3px;
    box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.2);
    align-items: center;
    background: linear-gradient(
        0deg,
        rgba(130, 188, 0, 0.1),
        rgba(130, 188, 0, 0.1)
      ),
      ${({ theme }) => theme.COLORS.BASE};
    ${({ theme }) =>
      applyResponsiveVdpStyles(theme, {
        mini: StyledTransportSectionVariants.mini.promo,
        expanded: StyledTransportSectionVariants.expanded.promo
      })}
  `
};

const StyledTransportInfoVariants = {
  mini: css`
    padding: 18px 0 0;
    flex-flow: wrap;
  `,
  expanded: css`
    margin-top: 15px;
  `
};

const transportMessageVariant = {
  promo: css`
    margin-left: 47px;
    margin-top: 10px;
  `
};

const StyledTransportMessageVariants = {
  mini: css`
    font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
    ${props => transportMessageVariant[props.variant]};
  `
};

const StyledTransportValueVariants = {
  mini: css`
    width: 217px;
    font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
    margin-top: 7px;
  `
};

const StyledRateInfoVariants = {
  mini: css`
    min-width: 75px;
  `
};

const StyledDealerProfileTextVariants = {
  mini: css`
    width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `
};

/**
 * Styled Components
 */
export const StyledTransportSection = styled.div`
  display: flex;
  box-shadow: ${isOmp ? 'none' : '0 1px 6px rgba(0, 0, 0, 0.2)'};
  border: ${({ theme }) =>
    isOmp ? `1px solid ${theme.COLORS.NEUTRAL_110}` : 'none'};
  border-radius: ${isOmp ? 6 : 3}px;
  flex-direction: column;
  flex-flow: wrap;
  ${props => StyledTransportSectionVariants[props.variant]};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTransportSectionVariants.mini.main,
      expanded: StyledTransportSectionVariants.expanded.main
    })}
`;

export const StyledTransportSeller = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`;

export const StyledTransportInfo = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTransportInfoVariants.mini,
      expanded: StyledTransportInfoVariants.expanded
    })}
`;

export const StyledTransportMessage = styled.span`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTransportMessageVariants.mini,
      expanded: StyledTransportMessageVariants.expanded
    })}
`;

export const StyledTransportMessageNone = styled(Detail.Label)``;

export const StyledTransportValue = styled.span`
  margin-left: auto;
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTransportValueVariants.mini
    })}
`;

export const StyledRateInfo = styled.a`
  margin-left: auto;
  text-decoration: underline;
  cursor: pointer;
  text-decoration-color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
  color: inherit;

  &:hover {
    color: ${({ theme }) => theme.COLORS.DARK_BLUE};
  }

  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledRateInfoVariants.mini
    })}
`;

export const StyledInformationIcon = styled(Icon).attrs({
  icon: 'TR5-Information_msg'
})`
  height: 18px;
  width: 18px;
  fill: ${({ theme }) => theme.COLORS.INFO};
  margin-right: 6px;
`;

export const StyledPromoOnIndicator = styled.div`
  display: block;
  width: 128px;
  height: 32px;
  background: #82bc00;
  border-radius: 3px;
  margin-right: 12px;
`;

export const StyledPromoOnIndicatorLabel = styled(Label.Bold)`
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE_SIZE};
  color: ${({ theme }) => theme.COLORS.BASE};
  line-height: 16px;
  margin-top: 7px;
  margin-left: 10px;
`;

export const StyledBreak = styled.br`
  display: block;
  margin-bottom: 7px;
  content: '';
`;

export const StyledDealer = styled.div`
  display: flex;
  flex-direction: row;

  a,
  span {
    display: block;
    word-break: break-word;
  }
`;

export const DealerProfileText = styled.div`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDealerProfileTextVariants.mini
    })}
`;

export const StyledRatings = styled.div`
  margin-left: 12px;
  display: flex;
  align-items: center;

  svg {
    margin-left: 6px;
  }
`;

export const StyledIcon = styled(Icon)`
  height: 18px;
  width: 18px;
`;

export const StyledRatingsCount = styled(Detail.Label)`
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  padding: 0;
  margin-left: 9px;
`;

export const StyledNoRatings = styled.p`
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  color: ${({ theme }) => theme.COLORS.NEUTRAL_30};
  margin: 0 0 0 18px;
  line-height: 19px;
`;
